import { useState, FC, useEffect } from "react";
import { ArrowLeft, CopyIcon } from "../../../../assets/icons";
import Avatar from "../../../../common/components/avatar/avatar";
import CommonDateRangePicker from "../../../../common/components/date-range-picker/CommonDateRangePicker";
import ToggleButton from "../../../../common/components/toggle-button/ToggleButton";
import { formatDate, getInitials, handleCopy } from "../../../../utils";
import styles from "./SolarProviderDetails.module.scss";
import { Range } from "react-date-range";
import SolarProviderCards from "./SolarProviderCards";
import TabSelector from "../../../../common/components/tab-selector/TabSelector";
import CommonPagination from "../../../../common/components/pagination/CommonPagination";
import SolarProviderOrder from "./SolarProviderOrder";
import SolarProviderProduct from "./SolarProviderProducts";
import {
	useLazyGetSolarProviderInformationQuery,
	useLazyGetSolarProviderProductsQuery,
	useToggleSolarProviderSuspentionMutation,
	useLazyGetSolarProviderOrdersQuery,
} from "../../../../service/admin-service";
import { ProviderBusinessInfoStatus, SolarProviderInfoQuery } from "../../../../model/provider/provider-dashboard-data";
import { STORE_KEY } from "../constant";
import { toast } from "react-toastify";
import { BounceLoader } from "react-spinners";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import ConfirmationDialog from "../../../../common/components/dialog/ConfirmationDialog";

export interface SolarProviderDetailsProps {
	onBackPress: () => void;
}

const SolarProviderDetails: FC<SolarProviderDetailsProps> = ({ onBackPress }) => {
	const dispatch = useDispatch();
	const date = new Date();
	const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
	const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
	const [range, setRange] = useState<Range>({
		startDate: firstDay,
		endDate: lastDay,
		key: "selection",
		showDateDisplay: false,
	});
	const [pageQuery, setPageQuery] = useState<SolarProviderInfoQuery>({});
	const pageSize = 6;
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);

	const [selectedTab, setSelectedTab] = useState(1);

	const [getProviderInfo, { data }] = useLazyGetSolarProviderInformationQuery();
	const [toggleSuspension, { isLoading: suspendingAction }] = useToggleSolarProviderSuspentionMutation();
	const [getSolarProviderProducts, { data: products, isFetching: productsLoading }] = useLazyGetSolarProviderProductsQuery();
	const [getOrders, { data: orders, isFetching: orderFetching }] = useLazyGetSolarProviderOrdersQuery();
	const [showToggleDialog, setShowToggleDialog] = useState(false);

	useEffect(() => {
		const providerCompanyId = localStorage.getItem(STORE_KEY);
		if (providerCompanyId) {
			setPageQuery({
				SolarProviderId: providerCompanyId,
				companyId: providerCompanyId,
				start: range?.startDate,
				end: range.endDate,
				page: currentPage,
				pageSize,
			});
		}
	}, []);

	useEffect(() => {
		if (pageQuery?.companyId) {
			fetchProviderInfo();
		}
	}, [pageQuery?.companyId, pageQuery?.start, pageQuery?.end]);

	useEffect(() => {
		if (pageQuery?.companyId && selectedTab === 1) {
			handleGetProducts();
		}
	}, [pageQuery?.page, pageQuery?.pageSize, selectedTab]);

	useEffect(() => {
		if (pageQuery?.companyId && selectedTab === 2) {
			handleGetOrders();
		}
	}, [pageQuery?.page, pageQuery?.pageSize, pageQuery?.start, pageQuery?.end, selectedTab]);

	const handleGetProducts = async () => {
		await getSolarProviderProducts(pageQuery)
			.unwrap()
			.then(res => {
				setTotalPages(res?.totalPages);
			})
			.catch(err => toast.error(err?.data?.title));
	};

	const fetchProviderInfo = async () => {
		dispatch(showLoader());
		await getProviderInfo(pageQuery)
			.unwrap()
			.then()
			.catch(err => toast.error(err?.data?.title as string))
			.finally(() => {
				dispatch(hideLoader());
			});
	};

	const handleSuspensionToggle = async () => {
		await toggleSuspension(pageQuery?.companyId ?? "")
			.unwrap()
			.then(() => {
				toast.success("suspension status updated");
				setShowToggleDialog(false);
			})
			.catch(err => toast.error(err?.data?.title as string));
	};

	const handleGetOrders = () => {
		getOrders(pageQuery)
			.unwrap()
			.then(res => {
				setTotalPages(res?.totalPages);
			})
			.catch(err => toast.error(err?.data?.title as string));
	};

	const handleDateRangeChange = (range: Range) => {
		if (range.startDate && range.endDate) {
			if (range.startDate.getTime() === range.endDate.getTime()) return;
			setRange(range);
			setPageQuery({ ...pageQuery, start: range.startDate, end: range.endDate });
		}
	};

	const tabs = [
		{ id: 1, title: "Products", onClick: () => changeTab(1) },
		{ id: 2, title: "Order", onClick: () => changeTab(2) },
	];

	const changeTab = (tabId: number) => {
		setSelectedTab(tabId);
		setCurrentPage(1);
	};

	const renderTabContent = () => {
		switch (selectedTab) {
			case 1:
				return <SolarProviderProduct products={products?.items} />;
			case 2:
				return <SolarProviderOrder orders={orders?.items} />;
			default:
				return (
					<>
						<SolarProviderProduct products={products?.items} />
					</>
				);
		}
	};

	return (
		<>
			{showToggleDialog && (
				<ConfirmationDialog
					title={`Are you sure you want to ${data?.isSuspended ? "activate" : "suspend"} this provider ?`}
					description="Kindly confirm before proceeding"
					onCloseDialog={() => setShowToggleDialog(false)}
					onConfirm={() => handleSuspensionToggle()}
					btnText="Proceed"
					actionLoading={suspendingAction}
				/>
			)}
			<div className={`${styles.headerSection} ${styles.standardSectionPadding}`}>
				<button style={{ cursor: "pointer" }} onClick={onBackPress}>
					<ArrowLeft />
				</button>
				<div className={styles.text}>Solar Provider Management</div>
			</div>
			<div className={styles.businessSection}>
				<div className={styles.businessNameSection}>
					<span className={styles.businessLabel}>Business Name</span>
					<span className={styles.breaker}></span>
					<span className={styles.businessName}>{data?.businessName}</span>
				</div>
				<div className={styles.toggleSection}>
					<label>Suspend Provider</label>
					{suspendingAction ? (
						<BounceLoader size={20} color="#DE841B" />
					) : (
						<ToggleButton className={styles.solarProviderToggle} onToggle={() => setShowToggleDialog(true)} value={data?.isSuspended} />
					)}
				</div>
				<div className={styles.businessInfoSection}>
					<div className={styles.innerSection}>
						<div className={styles.row}>
							<div className={styles.label}>Status</div>
							<div className={`${styles.labelValue}`}>
								{
									(
										() => {
											if(data?.status === ProviderBusinessInfoStatus.ACTIVE) {
												return <span className={`${styles.statusTag} ${styles.active}`}>{data?.status}</span>
											} else if(data?.status === ProviderBusinessInfoStatus.SUSPENDED) {
												return <span className={`${styles.statusTag} ${styles.suspended}`}>{data?.status}</span>
											} else {
												return <span className={`${styles.statusTag} ${styles.pending}`}>{data?.status}</span>
											}
										}
									)()
								}
							</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Address</div>
							<div className={styles.labelValue}>{data?.address}</div>
						</div>
						{data?.dateCreated && (
							<div className={styles.row}>
								<div className={styles.label}>Date Created</div>
								<div className={styles.labelValue}>{formatDate(new Date(data?.dateCreated))}</div>
							</div>
						)}
						{data?.phoneNumber && (
							<div className={styles.row}>
								<div className={styles.label}>Phone</div>
								<div className={`${styles.labelValue} ${styles.comboLabel}`}>
									{data?.phoneNumber}
									<CopyIcon height={16} onClick={() => handleCopy(data?.phoneNumber)} />
								</div>
							</div>
						)}
						<div className={styles.row}>
							<div className={styles.label}>Email address</div>
							<div className={styles.labelValue}>{data?.email}</div>
						</div>
						<div className={styles.row}>
							<div className={styles.label}>Contact Person</div>
							<div className={`${styles.labelValue} ${styles.comboLabel}`}>
								<Avatar placeholder={getInitials(data?.contactPerson ?? "")} /> {data?.contactPerson}
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={styles.dateRangeSection}>
				<CommonDateRangePicker initialDateRange={range} onChange={handleDateRangeChange} />
			</div>
			<div>
				<SolarProviderCards data={data} />
			</div>
			<div className={styles.tabSelectorWrapper}>
				<div className={styles.tabContainer}>
					<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
				</div>
				{renderTabContent()}
				<div className={styles.paginationContainer}>
					<CommonPagination
						currentPage={currentPage}
						isLoading={productsLoading || orderFetching}
						numberOfPages={totalPages}
						onPageChange={(page: number) => {
							setCurrentPage(page);
							setPageQuery({ ...pageQuery, page });
						}}
					/>
				</div>
			</div>
		</>
	);
};
export default SolarProviderDetails;
